import { motion } from "framer-motion";
import { ArrowUpRight, CalendarCheck, Copy, Instagram, Linkedin, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ChapterHead } from "./AboutSection";
import { RingQuarter } from "./GeoShapes";
import ContactForm from "./ContactForm";

const EMAIL = "fasinogiuseppe52@gmail.com";
const WHATSAPP_URL =
  "https://wa.me/393496170904?text=" +
  encodeURIComponent("Ciao Beppe! Ho visto il tuo portfolio e vorrei parlarti di un progetto Food & Beverage.");

const BehanceIcon = ({ className = "" }) => (
  <span className={`font-display font-bold leading-none ${className}`}>Bē</span>
);

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIALS = [
  {
    name: "Behance",
    handle: "peppefasino",
    url: "https://www.behance.net/peppefasino",
    testid: "contact-social-behance",
    accent: "hover:bg-blue-brand",
    Icon: BehanceIcon,
  },
  {
    name: "Instagram",
    handle: "@beppe_fasino",
    url: "https://instagram.com/beppe_fasino",
    testid: "contact-social-instagram",
    accent: "hover:bg-magenta-brand",
    Icon: Instagram,
  },
  {
    name: "TikTok",
    handle: "@beppefasino",
    url: "https://www.tiktok.com/@beppefasino",
    testid: "contact-social-tiktok",
    accent: "hover:bg-ink",
    Icon: TikTokIcon,
  },
  {
    name: "LinkedIn",
    handle: "Giuseppe Fasino",
    url: "https://www.linkedin.com/in/giuseppe-fasino/",
    testid: "contact-social-linkedin",
    accent: "hover:bg-ink",
    Icon: Linkedin,
  },
];

export default function ContactSection() {
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast.success("Email copiata negli appunti", { description: EMAIL });
    } catch {
      toast.info(EMAIL);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="relative bg-white">
      <RingQuarter color="#00C2E8" className="pointer-events-none absolute right-8 top-24 hidden h-14 w-14 lg:block" />
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
        <ChapterHead number="04" title="Parliamo del tuo progetto" testid="contact-heading" />

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <p className="max-w-lg text-base leading-relaxed text-neutral-600 sm:text-lg">
              Hai un prodotto di valore, ma un'identità che non lo racconta?
              Scrivimi: costruiamo insieme un sistema visivo che lo valorizzi,
              dallo scaffale al digitale. Risposta entro 24 ore, preventivi
              trasparenti, file sorgenti sempre inclusi.
            </p>

            <div className="mt-10 border border-ink">
              <div className="border-b border-ink bg-paper px-5 py-3 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                CONTATTI DIRETTI — RISPOSTA ENTRO 24H
              </div>
              <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="break-all font-display text-lg font-bold tracking-tight hover:text-blue-brand sm:text-xl"
                >
                  {EMAIL}
                </a>
                <div className="flex shrink-0 gap-3">
                  <button
                    data-testid="contact-copy-email-button"
                    onClick={copyEmail}
                    className="flex items-center gap-2 border border-ink px-4 py-2.5 font-mono text-xs tracking-wider transition-colors duration-300 hover:border-cyan-brand hover:bg-cyan-brand"
                  >
                    <Copy className="h-4 w-4" />
                    COPIA
                  </button>
                  <a
                    data-testid="contact-mailto-button"
                    href={`mailto:${EMAIL}?subject=Nuovo%20progetto%20F%26B%20—%20Richiesta%20preventivo`}
                    className="flex items-center gap-2 bg-ink px-4 py-2.5 font-mono text-xs tracking-wider text-paper transition-colors duration-300 hover:bg-magenta-brand"
                  >
                    <Mail className="h-4 w-4" />
                    SCRIVI ORA
                  </a>
                </div>
              </div>
              <div className="flex flex-col gap-4 border-t border-ink p-5 sm:flex-row sm:items-center sm:justify-between">
                <span className="font-mono text-xs tracking-wider text-neutral-500">
                  PREFERISCI UNA CHAT VELOCE?
                </span>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="flex items-center justify-center gap-2 bg-[#25D366] px-4 py-2.5 font-mono text-xs font-bold tracking-wider text-ink transition-colors duration-300 hover:bg-ink hover:text-[#25D366]"
                >
                  <MessageCircle className="h-4 w-4" />
                  WHATSAPP — 349 617 0904
                </a>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3 font-mono text-xs tracking-wider text-neutral-500">
              <span className="animate-led h-2 w-2 rounded-full bg-green-500" />
              DISPONIBILE PER NUOVI PROGETTI F&B — BARI, PUGLIA / REMOTO
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-px border border-neutral-200 bg-neutral-200 sm:grid-cols-2 lg:col-span-5">
            {SOCIALS.map((s, i) => (
              <motion.a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.testid}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`group flex flex-col justify-between bg-paper p-6 transition-colors duration-300 hover:text-paper ${s.accent}`}
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center border border-neutral-200 text-lg transition-colors duration-300 group-hover:border-paper/40">
                    <s.Icon className="h-5 w-5" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
                <div className="mt-8">
                  <span className="block font-display text-lg font-bold tracking-tight">
                    {s.name}
                  </span>
                  <span className="mt-1 block font-mono text-xs tracking-wider text-neutral-500 transition-colors duration-300 group-hover:text-paper/80">
                    {s.handle}
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 border border-ink"
          data-testid="contact-brief-panel"
        >
          <div className="border-b border-ink bg-ink px-5 py-3 font-mono text-[10px] tracking-[0.2em] text-paper">
            OPPURE COMPILA IL BRIEF — 30 SECONDI, NESSUN IMPEGNO
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="border-b border-ink bg-paper p-6 sm:p-10 lg:col-span-4 lg:border-b-0 lg:border-r">
              <h3 className="font-display text-xl font-bold tracking-tight">
                Come funziona
              </h3>
              <ul className="mt-6 space-y-5">
                {[
                  "Compili il brief in 30 secondi",
                  "Ti rispondo personalmente entro 24h",
                  "Preventivo su misura o call gratuita di 30 min",
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-4">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-ink font-mono text-[10px] text-paper">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm leading-relaxed text-neutral-600">{step}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex items-center gap-3 border-t border-neutral-200 pt-6 font-mono text-xs tracking-wider text-neutral-500">
                <CalendarCheck className="h-4 w-4 text-cyan-brand" />
                CALL GRATUITA PRENOTABILE DAL FORM
              </div>
            </div>
            <div className="lg:col-span-8">
              <ContactForm />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
